import { useState } from "react";
import { COLORS, BG_BASE } from "../../utils/colors";
import { CURRENT_DATE } from "../../utils/constants";
import { getCsatColor } from "../../utils/colorHelpers";

const sampleData = {
  score: 9.2,
  rawScore: 8.5,
  qualityMultiplier: 1.08,
  avgTeamCsat: 4.3,
  pairCoverage: { covered: 3, total: 3, pct: 100 },
  avgProjectsPerPair: 2.7,
  clusterBonus: 0.0,
  recencyBonus: 1.5,
  depthBonus: 0.5,
  pairs: [
    { members: ["Todd Korando", "Michael Monske"], count: 4, mostRecentDate: "2023-12-25", withinTwelveMonths: true, avgCsat: 4.5, qualityFactor: 0.86, engagements: [{ name: "Mobile Technology Roadmap", client: "River Valley Bank", roles: "PM + Senior Architect", date: "2022-03", csat: 4.8 }, { name: "RESTful Services Development", client: "Midwest Data Systems", roles: "Tech Lead + Senior Architect", date: "2023-01", csat: 4.2 }, { name: "Implementation of Business Intelligence", client: "Premier Healthcare", roles: "Support + Tech Analyst", date: "2023-12", csat: null }, { name: "Legacy System Modernization", client: "United Medical Group", roles: "PM + Doc Specialist", date: "2021-08", csat: 3.9 }] },
    { members: ["Todd Korando", "Shirley Cortez"], count: 2, mostRecentDate: "2023-08-07", withinTwelveMonths: true, avgCsat: 4.1, qualityFactor: 0.82, engagements: [{ name: "Operational Excellence Program", client: "CoreTech Systems", roles: "PM + Support Specialist", date: "2022-01", csat: 4.1 }, { name: "Digital Transformation of Change Management", client: "Cardinal Health", roles: "PM + Support", date: "2023-03", csat: null }] },
    { members: ["Shirley Cortez", "Michael Monske"], count: 2, mostRecentDate: "2023-11-20", withinTwelveMonths: true, avgCsat: null, qualityFactor: null, engagements: [{ name: "High Availability System Design", client: "Metro Healthcare", roles: "Support + Integration Engineer", date: "2023-09", csat: null }, { name: "Cloud Cost Optimization", client: "Midwest Insurance Group", roles: "Solution Architect + Senior Architect", date: "2023-07", csat: null }] },
  ],
  clusterEngagements: [],
};

function getRecencyLabel(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date)) return "";
  const ref = CURRENT_DATE;
  const monthsDiff = (ref.getFullYear() - date.getFullYear()) * 12 + (ref.getMonth() - date.getMonth());
  if (monthsDiff <= 1)  return "1 month ago";
  if (monthsDiff <= 12) return `${monthsDiff} months ago`;
  return `${Math.round(monthsDiff / 12)} year${Math.round(monthsDiff / 12) > 1 ? "s" : ""} ago`;
}

export default function CollaborationNetwork({ data, onEmployeeClick }) {
  const d = data || sampleData;
  const [showInfo, setShowInfo] = useState(false);
  if (!(d.pairs || []).length) return null;
  const coverageLabel = d.pairCoverage?.pct === 100 ? "Exceptional" : d.pairCoverage?.pct >= 67 ? "Strong" : "Moderate";

  return (
    <div style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>Collaboration Network Score</h3>
          <div style={{ position: "relative", display: "inline-block" }}>
            <span
              onMouseEnter={() => setShowInfo(true)}
              onMouseLeave={() => setShowInfo(false)}
              style={{ fontSize: 13, color: COLORS.textMuted, cursor: "help", userSelect: "none", lineHeight: 1 }}
            >
              ⓘ
            </span>
            {showInfo && (
              <div style={{
                position: "absolute", left: 0, top: 22, zIndex: 100,
                background: "#2A2A2A", border: `1px solid ${COLORS.cardBorder}`,
                borderRadius: 8, padding: "12px 14px", width: 290,
                fontSize: 11, color: COLORS.textMuted, lineHeight: 1.7,
                boxShadow: "0 4px 16px rgba(0,0,0,0.6)"
              }}>
                <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 8, fontSize: 12 }}>How this score is calculated</div>
                <div><span style={{ color: COLORS.green }}>Base (0–7)</span> — % of team pairs who have worked together before</div>
                <div><span style={{ color: COLORS.teal }}>Recency +0.5/pair</span> — collaborated within the last 12 months</div>
                <div><span style={{ color: COLORS.amber }}>Depth +0.5/pair</span> — 4 or more projects together</div>
                <div><span style={{ color: COLORS.purple }}>Cluster +1.0</span> — all members share at least one project</div>
                <div style={{ marginTop: 8, borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 8 }}>
                  <span style={{ color: COLORS.text }}>Quality multiplier (0.8×–1.2×)</span> — adjusts for outcomes: 60% weight on project CSAT, 40% on employee DIR. No data → neutral (1.0×).
                </div>
              </div>
            )}
          </div>
        </div>
        <span style={{ fontSize: 26, fontWeight: 700, color: COLORS.green }}>{(d.score || 0).toFixed(1)}</span>
      </div>

      <div style={{ width: "100%", height: 6, background: BG_BASE, borderRadius: 3, marginBottom: 8 }}>
        <div style={{ width: `${Math.min((d.score / 10) * 100, 100)}%`, height: "100%", background: `linear-gradient(90deg, ${COLORS.green}80, ${COLORS.green})`, borderRadius: 3 }} />
      </div>

      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16 }}>
        {coverageLabel}: {d.pairCoverage?.pct === 100 ? `All ${d.pairCoverage?.total} team pairs have collaboration history` : `${d.pairCoverage?.covered} of ${d.pairCoverage?.total} team pairs have collaboration history`}
      </div>

      {/* Summary stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Coverage",          value: `${d.pairCoverage?.pct || 0}%`,                                                                  color: COLORS.green  },
          { label: "Avg Projects/Pair", value: (d.avgProjectsPerPair || 0).toFixed(1),                                                          color: COLORS.purple },
          { label: "Recency Bonus",     value: `+${(d.recencyBonus || 0).toFixed(1)}`,                                                          color: COLORS.teal   },
          { label: "Depth Bonus",       value: `+${(d.depthBonus || 0).toFixed(1)}`,                                                            color: COLORS.amber  },
          { label: "Avg CSAT",          value: d.avgTeamCsat != null ? d.avgTeamCsat.toFixed(1) : "—",                                          color: d.avgTeamCsat != null ? getCsatColor(d.avgTeamCsat) : COLORS.textMuted },
        ].map((item, i) => (
          <div key={i} style={{ background: BG_BASE, borderRadius: 6, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>{item.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: item.color, marginTop: 2 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Pair cards — only show pairs that have actually worked together */}
      {(d.pairs || []).some(p => p.count > 0) && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Collaboration History</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {d.pairs.filter(p => p.count > 0).map((pair, i) => (
              <div key={i} style={{ background: BG_BASE, borderRadius: 8, padding: "14px 16px", border: `1px solid ${COLORS.cardBorder}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                    {(pair.members || []).map((name, idx) => (
                      <span key={name}>
                        {idx > 0 && " & "}
                        {onEmployeeClick
                          ? <span onClick={() => onEmployeeClick(name)} style={{ cursor: 'pointer', color: '#03DAC6' }} onMouseEnter={e => e.target.style.textDecoration='underline'} onMouseLeave={e => e.target.style.textDecoration='none'}>{name}</span>
                          : name}
                      </span>
                    ))}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {pair.mostRecentDate && (
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: pair.withinTwelveMonths ? `${COLORS.green}15` : `${COLORS.textMuted}15`, color: pair.withinTwelveMonths ? COLORS.green : COLORS.textMuted }}>
                        {pair.withinTwelveMonths ? "✓" : "⏱"} Last: {getRecencyLabel(pair.mostRecentDate)}
                      </span>
                    )}
                    {pair.avgCsat != null && (
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: `${getCsatColor(pair.avgCsat)}15`, color: getCsatColor(pair.avgCsat) }}>
                        Avg CSAT {pair.avgCsat.toFixed(1)}
                      </span>
                    )}
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 12, background: `${COLORS.green}20`, color: COLORS.green }}>
                      {pair.count} project{pair.count !== 1 ? "s" : ""} together
                    </span>
                  </div>
                </div>
                {(pair.engagements || []).length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 8 }}>
                    {pair.engagements.map((eng, j) => (
                      <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                          • {eng.client ? `${eng.client} / ` : ""}<span style={{ color: COLORS.text }}>{eng.name}</span>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0, fontSize: 10, color: COLORS.textMuted, alignItems: "center" }}>
                          {eng.roles && <span>{eng.roles}</span>}
                          {eng.date  && <span>{eng.date}</span>}
                          {eng.csat != null && (
                            <span style={{
                              padding: "1px 6px", borderRadius: 8, fontSize: 9, fontWeight: 600,
                              color: getCsatColor(eng.csat),
                              background: `${getCsatColor(eng.csat)}20`
                            }}>
                              {eng.csat.toFixed(1)}★
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {d.clusterEngagements?.length > 0 ? (
        <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 6, background: `${COLORS.green}10`, borderLeft: `3px solid ${COLORS.green}` }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.green }}>Cluster Detected: </span>
          <span style={{ fontSize: 12, color: COLORS.text }}>All team members worked together on {d.clusterEngagements.join(", ")}</span>
        </div>
      ) : (d.pairs || []).length > 0 && (
        <div style={{ marginTop: 12, fontSize: 11, color: COLORS.textMuted }}>No full-team cluster detected — pairs have worked together but not all simultaneously.</div>
      )}
    </div>
  );
}
